import {
  type FeedbackPosition,
  getSession,
  getSurvey,
  orUndefined,
  saveSurveyAnswers,
} from '@lms/api-client/backend';

export interface AnswerSurveyInput {
  slug: string;
  session: string;
  survey: number;
  position: FeedbackPosition;
  answers: Array<{ questionId: number; answer: number }>;
}

export type AnswerSurveyResult =
  | { kind: 'redirect'; to: string }
  | { kind: 'error'; message: string };

/**
 * Submit the trainee survey answers. Ports `answerSurvey` from the Next app's
 * features/survey/actions.ts (only the trainee submit path). The server action
 * redirected on success; here we return the target for the caller to navigate.
 */
export async function answerSurvey(
  input: AnswerSurveyInput,
): Promise<AnswerSurveyResult> {
  const { slug, session, survey, position, answers } = input;

  const surveyData = await orUndefined(getSurvey(survey));
  if (surveyData === undefined) {
    return { kind: 'error', message: 'Unknown survey' };
  }

  const ok = surveyData.questions.every(({ id }) =>
    answers.some(({ questionId }) => id === questionId),
  );
  if (!ok) {
    return { kind: 'error', message: 'You have to answer all questions!' };
  }

  const sessionDetails = await orUndefined(getSession(session));
  if (sessionDetails === undefined) {
    return { kind: 'error', message: 'Unknown session' };
  }

  await saveSurveyAnswers({
    surveyId: survey,
    courseId: sessionDetails.courseId,
    sessionId: session,
    position,
    answers,
  });

  if (position === 'before_course') {
    return { kind: 'redirect', to: `/${slug}/course/${session}` };
  }
  return { kind: 'redirect', to: `/${slug}/me/${sessionDetails.personalCode}` };
}
